<script setup lang="ts">
import { ref, defineProps, defineEmits, computed } from "vue";
import { useI18n } from "vue-i18n";
import DatePicker from "@/components/inputs/DatePicker.vue";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { formatLocalDate } from "@/utils/date";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import { BaziPredictionRequest } from "@/types/calculators";
import { PlaceSelectedPayload } from "@/types/google-places";

defineProps<{
  loading: boolean;
}>();

const emit = defineEmits<{
  submit: [BaziPredictionRequest];
}>();

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");

const birthDate = ref<Date | null>(null);
const birthTime = ref<string>("");
const gender = ref<string>("");
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);

const isValidInput = computed(() => {
  return (
    birthDate.value !== null &&
    birthTime.value.length > 0 &&
    longitude.value !== null &&
    timeZone.value !== null &&
    gender.value.length > 0
  );
});

const sexDropdownOptions = [
  { label: "calculators.bazi.labels.male", value: "1" },
  { label: "calculators.bazi.labels.female", value: "0" },
];

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

function onSubmit() {
  if (isValidInput.value) {
    emit("submit", {
      date: formatLocalDate(birthDate.value as Date),
      time: birthTime.value,
      gender: gender.value,
      longitude: longitude.value as number,
      timezone: timeZone.value as string,
    });
  }
}
</script>

<template>
  <div class="bg-white rounded-[20px] px-10 py-6">
    <h4 class="text-[40px] leading-100p font-medium text-left text-brown mb-10">
      {{ t("calculators.bazi.title") }}
    </h4>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 md:pb-10">
      <!-- BIRTHDAY  -->
      <div class="flex flex-col items-start">
        <p class="label-styles">
          {{ t("calculators.bazi.labels.birthday") }}
        </p>
        <DatePicker
          id="birthday"
          @dateSelected="(newDate: Date) => (birthDate = newDate)"
        />
      </div>

      <!-- BIRTHTIME -->
      <div class="flex flex-col items-start">
        <p class="label-styles">
          {{ t("calculators.bazi.labels.birthtime") }}
        </p>
        <input
          id="birthtime"
          v-model="birthTime"
          type="time"
          class="border border-divider pl-4 pr-[6px] w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted"
        />
      </div>

      <div class="flex flex-col items-start">
        <p class="label-styles">
          {{ t("calculators.bazi.labels.place") }}
        </p>
        <GooglePlaceAutocompleteInput @placeSelected="handlePlaceSelected" />
      </div>

      <!-- Пол -->
      <div class="flex flex-col items-start">
        <label for="sex" class="label-styles">
          {{ t("calculators.bazi.labels.sex") }}
        </label>
        <select
          id="sex"
          v-model="gender"
          class="border border-divider pl-4 pr-4 w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted cursor-pointer"
        >
          <option
            v-for="item in sexDropdownOptions"
            :key="item.value"
            :value="item.value"
          >
            {{ t(item.label) }}
          </option>
        </select>
      </div>
    </div>

    <!-- CALCULATE BUTTON -->
    <div
      class="xl:border-t border-ternary flex items-center justify-center pt-6"
    >
      <GradientButton
        class="w-[191px] h-[56px]"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('buttons.calculate')"
        @click="onSubmit"
        :is-loading="loading"
        :is-disabled="!isValidInput"
      />
    </div>
  </div>
</template>

<style scoped>
.label-styles {
  @apply mb-2 font-semibold text-base leading-[20px] text-brown;
}
</style>
